import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { Subscription, forkJoin, from } from 'rxjs';
import {
  Address,
  ApConfig,
  CompanyVendor,
  Contact,
  RequestJob,
  Vendor
} from 'src/app/classes/models';
import { BusinessComplianceSetting } from 'src/app/classes/models/business-compliance-setting';
import { ComplianceFinding } from 'src/app/classes/models/compliance-finding';
import { ComplianceSetting } from 'src/app/classes/models/compliance-setting';
import { Note } from 'src/app/classes/models/note';
import { ViveProgress } from 'src/app/classes/models/vive-progress';
import { ComplianceReviewAction } from 'src/app/interfaces/compliance-review-action';
import { ComplianceResultStatuses } from 'src/app/interfaces/compliance/compliance-result-statuses';
import { BusinessInfoComplianceResults } from 'src/app/interfaces/compliance/results/business-info/business-info-compliance-results';
import { NewBusinessInfoComplianceResults } from 'src/app/interfaces/compliance/results/business-info/new-business-info-compliance-results';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { AddEditVendorInformationModalComponent } from 'src/app/modals/add-edit-vendor-information-modal/add-edit-vendor-information-modal.component';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { NoteModalComponent } from 'src/app/modals/note-modal/note-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceStepStatusService } from 'src/app/services/compliance-step-status.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { ReportService } from 'src/app/services/report.service';
import { RequestJobService } from 'src/app/services/request-job.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';
import { ViveProgressService } from 'src/app/services/vive-progress.service';
import { businessVerificationApiErrorsService } from 'src/app/shared/vive-api/business-verification-api-errors.service';
import { companyVendorsService } from 'src/app/shared/vive-api/company-vendors.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

@Component({
  selector: 'app-compliance-business-review',
  templateUrl: './compliance-business-review.component.html',
  styleUrls: ['./compliance-business-review.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceBusinessReviewComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private ComplianceService: ComplianceService,
    private route: ActivatedRoute,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private SharedDataService: SharedDataService,
    private ReportService: ReportService,
    private RequestJobService: RequestJobService,
    private AuthService: AuthService,
    private VendorService: VendorService,
    private ViveProgressService: ViveProgressService,
    private ComplianceStepStatusService: ComplianceStepStatusService
  ) {
    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (role && new Set([...this.maskedSSNPermissions]).has(role)) {
      this.hasSSNAccess = true;
    }
  }

  SharedDataServiceSubscription!: Subscription;

  CompanyId!: number;
  VendorId!: number;
  CompanyComplianceTypeId!: number;
  vendorRiskLevel!: number;
  // Used to display text for Admin while running business verification.
  isRunningBusinessVerification: boolean = false;

  // We capture an instance of the Auto Approve modal to ensure we only display 1 at a time.
  AutoApproveModal?: NgbModalRef;

  // Determines if the Vendor is linked to at least 1 AP Vendor in the Client's list
  // of AP Vendors.
  hasApVendor?: boolean;
  LocalizationInfo!: LocalizationInfo;
  ApConfigs: ApConfig[] = [];
  Vendor!: Vendor;
  ComplianceContact!: Contact;
  StreetAddress!: Address;
  MailingAddress!: Address;
  CompanyVendor!: CompanyVendor;
  ComplianceSetting?: ComplianceSetting;
  BusinessComplianceSetting?: BusinessComplianceSetting;
  ComplianceFinding: ComplianceFinding | null = null;
  ComplianceProgress?: ViveProgress | null;
  action!: ComplianceReviewAction;
  // Flag used to determine if report data exists for this Vendor.
  hasBusinessReportData: boolean = false;

  RequestJobs: RequestJob[] = [];

  // Old business info compliance results.
  BusinessInfoResults?: BusinessInfoComplianceResults;
  // New business info compliance results.
  NewBusinessInfoResults?: NewBusinessInfoComplianceResults;

  ComplianceFindingForm = new FormGroup({
    id: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true }
    ),
    VendorId: new FormControl<number | null | undefined>(null, {
      validators: [Validators.required]
    }),
    companyIdentity: new FormControl<number | null>(null),
    companyWatchlist: new FormControl<number | null>(null),
    companyInGoodStanding: new FormControl<boolean | null>(null),
    bankruptcy: new FormControl<number | null>(null),
    companyLiens: new FormControl<number | null>(null),
    w9Check: new FormControl<boolean | null>(null),

    companyBusinessVerified: new FormControl<boolean | null>(null),
    companyGovernmentWatchlist: new FormControl<number | null>(null),
    companyAddress: new FormControl<
      'Verified' | 'Not Matching' | 'Not Required' | null
    >(null),
    companySecretaryOfState: new FormControl<
      | 'Not Required'
      | 'In Good Standing'
      | 'Inactive'
      | 'Not In Good Standing'
      | 'Manual Review Needed'
      | 'No Records Found'
      | null
    >(null),
    companyBankruptcy: new FormControl<number | null>(null),
    companyLiensAndJudgments: new FormControl<number | null>(null),
    companyW9Check: new FormControl<boolean | null>(null)
    // companyRequester: new FormControl<string | null>(null)
  });

  /*
  $scope.requesterVals = [
    { display: '', value: null },
    { display: 'Not Entered', value: 'Not Entered' },
    { display: 'Matching', value: 'Matching' },
    { display: 'Likely Matching', value: 'Likely Matching' },
    { display: 'Unknown', value: 'Unknown' },
    { display: 'Not Matching', value: 'Not Matching' }
  ];
  */

  canadianIdentityValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No', value: 0 },
    { label: 'Yes', value: 30 }
  ];

  identityValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: '0', value: 0 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
    { label: '50', value: 50 }
  ];

  yesNoValues: { label: string; value: boolean | null }[] = [
    { label: '', value: null },
    { label: 'No', value: false },
    { label: 'Yes', value: true }
  ];

  watchlistValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Watchlists', value: 0 },
    { label: 'Watchlist Found', value: 1 }
  ];

  bankruptcyValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Bankruptcies', value: 0 },
    { label: 'Forgiven Bankruptcies', value: -1 },
    { label: '0 - 1 Year Post Decisions', value: 1 },
    { label: '2 Years Post Decisions', value: 2 },
    { label: '3 Years Post Decisions', value: 3 },
    { label: '4 Years Post Decisions', value: 4 },
    { label: '5 Years Post Decisions', value: 5 },
    { label: '6 Years Post Decisions', value: 6 },
    { label: '7 Years Post Decisions', value: 7 },
    { label: '8+ Years Post Decisions', value: 8 }
  ];

  addressValues: { label: string; value: string | null }[] = [
    { label: '', value: null },
    { label: 'Verified', value: 'Verified' },
    { label: 'Not Matching', value: 'Not Matching' }
  ];

  secretaryOfStateValues: { label: string; value: string | null }[] = [
    { label: '', value: null },
    { label: 'Not Required', value: 'Not Required' },
    { label: 'In Good Standing', value: 'In Good Standing' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Not In Good Standing', value: 'Not In Good Standing' },
    { label: 'Manual Review Needed', value: 'Manual Review Needed' },
    { label: 'No Records Found', value: 'No Records Found' }
  ];

  LoggedInUser = this.AuthService.getUser();

  maskedSSN?: string;
  fullSSN?: string;
  hasSSNAccess: boolean = false;

  maskedSSNPermissions = [
    'ADMIN_ADMIN',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM',
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_INSURANCE_TEAM'
  ];

  viewSSN() {
    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast('Error', 'Missing user role.', {
        returnError: true
      });
    }

    if (new Set(this.maskedSSNPermissions).has(role)) {
      return this.VendorService.getMaskedVendorSSN(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.VendorId,
          attributes: ['id'],
          aliasedAttributes: ['maskedSSN']
        })
      ).subscribe({
        next: (Vendor) => {
          if (!Vendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor.',
              { returnError: true }
            );
          }

          const { maskedSSN } = Vendor;
          Object.assign(this, { maskedSSN });
          this._cdr.markForCheck();
        }
      });
    }

    return;
  }

  checkForPassedFindings() {
    if (!this.CompanyVendor) {
      return false;
    }

    const { isUsingNewBusinessVerification, needsFeeWaiveRenewal } =
      this.CompanyVendor;

    if (needsFeeWaiveRenewal) {
      return false;
    }

    if (isUsingNewBusinessVerification === undefined) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There was an error processing business verification results.',
        { returnError: true }
      );
    }

    if (isUsingNewBusinessVerification) {
      if (!this.NewBusinessInfoResults) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error processing updated business verification results.',
          { returnError: true }
        );
      }

      const BusinessInfoResultKeys = Object.keys(
        this.NewBusinessInfoResults
      ) as (keyof NewBusinessInfoComplianceResults)[];

      return (
        BusinessInfoResultKeys.length > 0 &&
        BusinessInfoResultKeys.every((resultKey) =>
          ['PASSED', 'NOT REQUIRED'].includes(
            this.NewBusinessInfoResults![resultKey]
          )
        )
      );
    } else {
      if (!this.BusinessInfoResults) {
        throw this.ToastService.addErrorToast(
          'Error',
          'There was an error processing legacy business verification results.',
          { returnError: true }
        );
      }

      const BusinessInfoResultKeys = Object.keys(
        this.BusinessInfoResults
      ) as (keyof BusinessInfoComplianceResults)[];

      return (
        BusinessInfoResultKeys.length > 0 &&
        BusinessInfoResultKeys.every((resultKey) =>
          ['PASSED', 'NOT REQUIRED'].includes(
            this.BusinessInfoResults![resultKey]
          )
        )
      );
    }
  }

  runBusinessVerification() {
    this.isRunningBusinessVerification = true;
    this.ComplianceService.runBusinessVerificationOnVendor(
      this.CompanyId,
      { VendorId: this.VendorId },
      { waitForJobs: true }
    ).subscribe({
      next: ([Vendor, ComplianceResults]) => {
        this.ViveProgressService.getViveProgress({
          CompanyId: this.CompanyId,
          VendorId: this.VendorId,
          type: 'Vendor Info'
        }).subscribe({
          next: (ComplianceProgress) => {
            const {
              lastRanBusinessVerificationReportDate,
              hasBusinessReportData,
              ComplianceFinding
            } = Vendor;

            Object.assign(this.Vendor, {
              lastRanBusinessVerificationReportDate
            });

            if (hasBusinessReportData === undefined) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error loading Vendor data.',
                { returnError: true }
              );
            }

            this.hasBusinessReportData = hasBusinessReportData;

            if (ComplianceFinding) {
              this.ComplianceFinding = ComplianceFinding;
              this.ComplianceFindingForm.patchValue(ComplianceFinding);
            }

            const { viveStatus } = ComplianceResults;

            this.SharedDataService.sendEvent(
              'UpdateCompanyVendor',
              new CompanyVendor({ viveStatus })
            );

            this.recalculateComplianceResults(ComplianceResults);
            this.isRunningBusinessVerification = false;

            if (ComplianceProgress) {
              this.SharedDataService.sendEvent(
                'SetUpdatedComplianceProgress',
                new ViveProgress(ComplianceProgress)
              );
            }
            this._cdr.markForCheck();
          }
        });
      },
      error: () => {
        this.isRunningBusinessVerification = false;
        this._cdr.markForCheck();
      }
    });
  }

  getStuckRequestJobs() {
    this.RequestJobService.getRequestJobs(
      'data',
      this.UtilityService.stringifyObjectsInQueryObject({
        status: 'PROCESSING'
      })
    ).subscribe({
      next: (RequestJobs) => {
        this.RequestJobs = RequestJobs;
        this._cdr.markForCheck();
      }
    });
  }

  clearBusinessVerificationErrors() {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'This will remove all business verification errors. Do you wish to continue?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          from(
            businessVerificationApiErrorsService.archiveAllForVendor({
              data: { VendorId: this.VendorId },
              options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
            })
          ).subscribe({
            next: () => {
              if (this.Vendor && this.Vendor.BusinessVerificationApiErrors) {
                this.Vendor.BusinessVerificationApiErrors.splice(0);
              }
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed business verification errors.'
              );
            }
          });
        }
      }
    });
  }

  resetStuckVerification() {
    if (this.RequestJobs.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot reset stuck request jobs. No request jobs found.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'This will clear any actively running Business Verification jobs. Do you wish to continue?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          const requestJobsToSave = this.RequestJobs.map((job) => {
            return this.RequestJobService.saveRequestJob(
              Object.assign(job, { status: 'REJECTED' })
            );
          });

          forkJoin(requestJobsToSave).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully cleared running jobs.'
              );
              this.getStuckRequestJobs();
              this._cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  // Allows finding override with a note.
  overrideComplianceFindings() {
    const NoteModal = this.ModalService.open(NoteModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'note-modal'
    });
    const { componentInstance } = NoteModal;

    const titleOptions: string[] = [
      'Vendor Information-Override',
      'Vendor Information-Edits',
      'Vendor Information - Waive/Cure'
    ];

    Object.assign(componentInstance, {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      titleOptions
    });

    from(NoteModal.result).subscribe({
      next: ({ Note }: { Note: Note }) => {
        const ComplianceFinding = this.ComplianceFindingForm.value;
        this.ComplianceService.overrideComplianceFinding(
          { VendorId: this.VendorId },
          { ComplianceFinding, Note },
          { CompanyId: this.CompanyId }
        ).subscribe({
          next: ({ Note, ComplianceFinding }) => {
            const { id: CompanyVendorId } = this.CompanyVendor;

            forkJoin([
              this.ViveProgressService.getViveProgress({
                CompanyVendorId,
                type: 'Vendor Info'
              }),
              this.ComplianceService.getComplianceResults(
                this.CompanyId,
                this.VendorId
              )
            ]).subscribe({
              next: ([BusinessInfoComplianceProgress, ComplianceResults]) => {
                this.SharedDataService.sendEvent(
                  'SetUpdatedComplianceProgress',
                  new ViveProgress(BusinessInfoComplianceProgress)
                );
                this.recalculateComplianceResults(ComplianceResults);

                this.ComplianceFinding = ComplianceFinding;
                this.ComplianceFindingForm.patchValue(ComplianceFinding);

                this.ToastService.addSuccessToast(
                  'Success!',
                  'Successfully overrided Compliance Findings.'
                );

                this._cdr.markForCheck();
              }
            });
          }
        });
      }
    });
  }

  getNewWatchlistRequirementLabel(watchlistNumber: number | null) {
    if (watchlistNumber === null) {
      return 'Not Required';
    }

    if (watchlistNumber === 0) {
      return 'None Allowed';
    }

    return 'Allowed';
  }

  getCorporateLiensLabel(lienNumber: number) {
    return this.LocalizationInfo.moneySymbol.label.concat(
      lienNumber.toString()
    );
  }

  getWatchlistRequirementLabel(watchlistNumber: number | null) {
    const watchlistToLabelMap: { [key: number | string]: string } = {
      null: '',
      0: 'No Watchlists',
      1: '1 Watchlist',
      2: 'Multiple Watchlists'
    };

    if (watchlistNumber === null) {
      return watchlistToLabelMap['null'];
    }

    return watchlistToLabelMap[watchlistNumber];
  }

  getBankruptcyRequirementLabel(numberOfYears: number) {
    const yearToLabelMap: { [key: number]: string } = {
      0: 'No Bankruptcies',
      [-1]: 'Forgiven Bankruptcies',
      1: '0 - 1 Year Post Decisions',
      2: '2 Years Post Decisions',
      3: '3 Years Post Decisions',
      4: '4 Years Post Decisions',
      5: '5 Years Post Decisions',
      6: '6 Years Post Decisions',
      7: '7 Years Post Decisions',
      8: '8+ Years Post Decisions'
    };

    return yearToLabelMap[numberOfYears];
  }

  checkIfNullOrUndefined(value: any) {
    return ['null', 'undefined'].includes(typeof value);
  }

  isNotRequiredResult(result: ComplianceResultStatuses | 'NO EXACT MATCH') {
    return result === 'NOT REQUIRED';
  }

  getNumberOfEmployeesLabel(numberOfEmployees: number) {
    const employeeQuantityToLabelMap: { [key: number]: string } = {
      0: '0 Employees',
      1: '1 Employee',
      2: '2 Employees',
      3: '3 Employees',
      4: '4 Employees',
      5: '5 - 10 Employees',
      10: '11 - 25 Employees',
      25: '26 - 50 Employees',
      50: '51 - 100 Employees',
      100: '101 - 500 Employees',
      500: '501+ Employees'
    };

    return employeeQuantityToLabelMap[numberOfEmployees] || '';
  }

  displayAutoApproveModal() {
    if (this.LoggedInUser && this.Vendor && this.CompanyVendor) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;
      const { lastRanBusinessVerificationReportDate } = this.Vendor;
      const { viveStatus } = this.CompanyVendor;

      if (
        role &&
        new Set([
          'ADMIN_ADMIN',
          'ADMIN_FRAUD_TEAM',
          'ADMIN_PROJECT_MANAGEMENT_TEAM'
        ]).has(role) &&
        // Ensure there either is no report (overridden findings) or the report isn't eleven months old.
        (!lastRanBusinessVerificationReportDate ||
          !this.isReportElevenMonthsOldOrOlder(
            lastRanBusinessVerificationReportDate
          )) &&
        this.checkForPassedFindings() &&
        (this.ComplianceProgress === null ||
          (this.ComplianceProgress &&
            !['Passed', 'Failed'].includes(this.ComplianceProgress.status!))) &&
        ![
          'Inactive',
          'Unregistered',
          'Registering',
          'Awaiting Payment'
        ].includes(viveStatus!) &&
        !this.AutoApproveModal
      ) {
        this.AutoApproveModal = this.ModalService.open(ConfirmModalComponent, {
          backdrop: 'static',
          modalDialogClass: 'confirm-modal'
        });
        document.body.style.overflowY = 'auto';
        Object.assign(this.AutoApproveModal.componentInstance, {
          // VPDW-4199: scoped to this step, and names any sibling step still needing
          // attention so this never reads as a review-wide all-clear.
          displayText:
            this.ComplianceStepStatusService.buildStepApprovalText(
              'Vendor Info'
            )
        });

        const { result } = this.AutoApproveModal;

        from(result).subscribe({
          next: (result) => {
            if (result) {
              delete this.AutoApproveModal;
              this.SharedDataService.sendEvent(
                'UpdateComplianceProgress',
                new ViveProgress({ status: 'Passed', type: 'Vendor Info' })
              );
              this.SharedDataService.sendEvent('GoToNextStep');
            }
          }
        });
      }
    }
  }

  recalculateComplianceResults(ComplianceResults: any) {
    const BusinessInfoProperties = [
      'companyIdentity',
      'companyInGoodStanding',
      'companyLiens',
      'companyWatchlist',
      'bankruptcy',
      'w9Check'
    ];

    this.BusinessInfoResults =
      this.UtilityService.constructObjectFromObjectProperties(
        ComplianceResults,
        BusinessInfoProperties
      ) as BusinessInfoComplianceResults;

    const NewBusinessInfoProperties = [
      'addressResults',
      'bankruptcyResults',
      'businessVerifiedResults',
      'governmentWatchlistResults',
      'lienResults',
      'secretaryOfStateResults',
      // This field is only used for business verified Vendors.
      // 'requesterResults',
      'w9Results'
    ];

    this.NewBusinessInfoResults =
      this.UtilityService.constructObjectFromObjectProperties(
        ComplianceResults,
        NewBusinessInfoProperties
      ) as NewBusinessInfoComplianceResults;
    this.displayAutoApproveModal();
  }

  downloadBusinessVerificationReport() {
    this.ReportService.generateBusinessVerificationReport({
      CompanyId: this.CompanyId,
      VendorId: this.VendorId
    }).subscribe({
      next: (ReportData) => {
        const { legalName } = this.Vendor;
        FileSaver.saveAs(
          new Blob([ReportData]),
          `${legalName} Business Verification Report.pdf`
        );
      }
    });
  }

  downloadRawDataReports() {
    forkJoin([
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        reqType: 'BusinessInstantID',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        reqType: 'BusinessInstantID2',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        reqType: 'CorporateSearch2Request',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        reqType: 'CorporateReport2Request',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getIRSReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        requestType: 'Tin Match',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getTinCheckReport({
        options: JSON.stringify({ limit: 1 }),
        VendorId: this.VendorId,
        requestType: 'ValidateTinNameAddressListMatch',
        order: JSON.stringify([['createdAt', 'DESC']])
      })
    ]).subscribe({
      next: ([
        LexNexReportInstantId,
        LexNexReportInstantId2,
        LexNexReportCorporateSearch,
        LexNexReportCorporateReport,
        IRSReport,
        TinCheckReport
      ]) => {
        if (IRSReport) {
          const { responseJSON } = IRSReport;
          if (!responseJSON) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing response data from IRS Report.',
              { returnError: true }
            );
          }
          const formattedReportData = JSON.stringify(responseJSON, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-IRSRawData.txt`
          );
        }
        if (TinCheckReport) {
          const { responseData } = TinCheckReport;
          if (!responseData) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing response data from Tin Check report.',
              { returnError: true }
            );
          }
          // Hack, use JSON parse to parse XML data into formatted string.
          let formattedReportData = JSON.stringify(responseData).replace(
            /\\r\\n/g,
            '\r\n'
          );
          formattedReportData = formattedReportData.slice(
            1,
            formattedReportData.length - 1
          );
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-TinCheckRawData.txt`
          );
        }
        if (LexNexReportInstantId) {
          const { resJson } = LexNexReportInstantId;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexInstantIDRawData.txt`
          );
        }
        if (LexNexReportInstantId2) {
          const { resJson } = LexNexReportInstantId2;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexInstantID2RawData.txt`
          );
        }
        if (LexNexReportCorporateSearch) {
          const { resJson } = LexNexReportCorporateSearch;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexReportCorporateSearchRawData.txt`
          );
        }
        if (LexNexReportCorporateReport) {
          const { resJson } = LexNexReportCorporateReport;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${this.Vendor.legalName}-LexNexReportCorporateReportRawData.txt`
          );
        }
      }
    });
  }

  editVendorInformation() {
    const AddEditVendorInformationModal = this.ModalService.open(
      AddEditVendorInformationModalComponent,
      {
        backdrop: 'static',
        modalDialogClass: 'add-edit-vendor-information-modal'
      }
    );
    const { componentInstance, result } = AddEditVendorInformationModal;
    Object.assign(componentInstance, {
      VendorId: this.VendorId,
      CompanyId: this.CompanyId
    });

    from(result).subscribe({
      next: (SavedVendor: Vendor) => {
        Object.assign(this.Vendor, SavedVendor);
        this.SharedDataService.sendEvent('SetVendor', new Vendor(SavedVendor));
        this._cdr.markForCheck();
      }
    });
  }

  isReportElevenMonthsOldOrOlder(lastRanReportDate: Date) {
    return (
      DateTime.fromJSDate(new Date()).diff(
        DateTime.fromJSDate(lastRanReportDate),
        'months'
      ).months >= 11
    );
  }

  ngOnDestroy() {
    // Need to unsubscribe from shared data service when component is destroyed to prevent
    // potential events from firing when next component is instantiated.
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    const ParamMap = this.route.snapshot.paramMap;

    const CompanyId = Number(ParamMap.get('CompanyId'));
    const VendorId = Number(ParamMap.get('VendorId'));
    const CompanyComplianceTypeId = Number(
      ParamMap.get('CompanyComplianceTypeId')
    );

    if (!CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!CompanyComplianceTypeId) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Compliance Type Id.',
        { returnError: true }
      );
    }

    Object.assign(this, { CompanyId, VendorId, CompanyComplianceTypeId });

    this.ComplianceFindingForm.patchValue({ VendorId: this.VendorId });

    this.loadComplianceData();
  }

  /**
   * The primary CompanyVendor query uses required inner joins (compliance
   * setting, street/mailing address). When any join misses, Sequelize returns
   * null — run targeted follow-up loads so the toast names the actual gap.
   */
  private diagnoseMissingCompanyVendorLoadFailure(
    vendorRiskLevel: number,
    countOfArchivedClientVendorRelationships: number
  ): void {
    const baseCompanyVendorQuery: { [key: string]: unknown } = {
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      attributes: JSON.stringify(['id'])
    };

    if (countOfArchivedClientVendorRelationships > 0) {
      baseCompanyVendorQuery['isArchived'] = true;
    }

    forkJoin({
      companyVendor: from(
        companyVendorsService.findOneByCompanyAndVendor({
          pathParams: {
            companyId: this.CompanyId!,
            vendorId: this.VendorId!
          }
        })
      ),
      vendor: this.VendorService.getVendorV2(this.VendorId, {
        attributes: JSON.stringify(['id']),
        StreetAddress: JSON.stringify({ options: { required: false } }),
        MailingAddress: JSON.stringify({ options: { required: false } })
      }),
      complianceProbe: this.CompanyVendorService.getCompanyVendor({
        ...baseCompanyVendorQuery,
        Company: JSON.stringify({
          options: { required: true },
          attributes: ['id'],
          ComplianceSetting: {
            options: { required: false },
            attributes: ['id'],
            CompanyComplianceTypeId: this.CompanyComplianceTypeId,
            riskLevel: vendorRiskLevel
          }
        })
      })
    }).subscribe({
      next: ({ companyVendor, vendor, complianceProbe }) => {
        if (!companyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing client/vendor relationship.',
            { returnError: true }
          );
        }

        if (!vendor?.StreetAddress) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Vendor is missing street address information.',
            { returnError: true }
          );
        }

        if (!vendor?.MailingAddress) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Vendor is missing Mailing Address information.',
            { returnError: true }
          );
        }

        const complianceSettings =
          complianceProbe?.Company?.ComplianceSettings ?? [];

        if (
          !Array.isArray(complianceSettings) ||
          complianceSettings.length === 0
        ) {
          throw this.ToastService.addErrorToast(
            'Error',
            "Missing client compliance settings for this vendor's risk tier. Verify the client's compliance settings have been configured.",
            { returnError: true }
          );
        }

        throw this.ToastService.addErrorToast(
          'Error',
          'Missing client/vendor information required to load business review.',
          { returnError: true }
        );
      },
      error: () => {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing client/vendor information required to load business review.',
          { returnError: true }
        );
      }
    });
  }

  private loadComplianceData(): void {
    forkJoin([
      // Determine if client/vendor relationship is archived, if so, display archived data.
      this.CompanyVendorService.getCompanyVendorCount({
        CompanyId: this.CompanyId,
        VendorId: this.VendorId,
        isArchived: true
      }),
      this.ComplianceService.getTierLevelForVendorV2(
        this.CompanyId,
        this.VendorId,
        this.CompanyComplianceTypeId
      )
    ]).subscribe({
      next: ([countOfArchivedClientVendorRelationships, vendorRiskLevel]) => {
        const companyVendorQuery = {
          CompanyId: this.CompanyId,
          VendorId: this.VendorId,
          Company: JSON.stringify({
            options: { required: true },
            attributes: ['id', 'legalName'],
            ComplianceSetting: {
              options: { required: true },
              CompanyComplianceTypeId: this.CompanyComplianceTypeId,
              riskLevel: vendorRiskLevel
            },
            BusinessComplianceSetting: {
              options: { required: false },
              CompanyComplianceTypeId: this.CompanyComplianceTypeId
            },
            ApConfig: {
              options: { required: false },
              ApVendor: {
                options: { required: false },
                VendorId: this.VendorId
              }
            }
          }),
          Vendor: JSON.stringify({
            options: { required: true },
            excludedAttributes: ['logo'],
            aliasedAttributes: [
              'lastRanBusinessVerificationReportDate',
              'hasBusinessReportData',
              'hasProfessionalTrade',
              'hasNonProfessionalTrade',
              'hasProfessionalOrNonProfessionalTrade',
              'hasSSN'
            ],
            BusinessVerificationApiError: {},
            ComplianceFinding: {
              options: { required: false }
            },
            StreetAddress: {
              options: { required: true }
            },
            MailingAddress: {
              options: { required: true }
            },
            ComplianceContact: {
              isCompliance: true
            }
          })
        };

        if (countOfArchivedClientVendorRelationships > 0) {
          Object.assign(companyVendorQuery, { isArchived: true });
        }

        forkJoin([
          this.CompanyVendorService.getCompanyVendor(companyVendorQuery),
          this.ComplianceService.getComplianceResults(
            this.CompanyId,
            this.VendorId
          )
        ]).subscribe({
          next: ([CompanyVendor, ComplianceResults]) => {
            if (!CompanyVendor) {
              this.diagnoseMissingCompanyVendorLoadFailure(
                vendorRiskLevel,
                countOfArchivedClientVendorRelationships
              );
              return;
            }

            this.CompanyVendor = CompanyVendor;

            const { Company, Vendor, isUsingNewBusinessVerification } =
              this.CompanyVendor;

            if (!Company) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Client information.',
                { returnError: true }
              );
            }

            if (!Vendor) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Vendor information.',
                { returnError: true }
              );
            }

            const {
              ComplianceSettings,
              BusinessComplianceSettings,
              ApConfigs
            } = Company;

            if (!Array.isArray(ComplianceSettings)) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Compliance Settings.',
                { returnError: true }
              );
            }

            if (ComplianceSettings.length > 1) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Compliance Setting was incorrectly loaded.',
                { returnError: true }
              );
            }

            if (!Array.isArray(BusinessComplianceSettings)) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing Business Compliance Settings.',
                { returnError: true }
              );
            }

            if (BusinessComplianceSettings.length > 1) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Business Compliance Setting was incorrectly loaded.',
                { returnError: true }
              );
            }

            if (Array.isArray(ApConfigs)) {
              this.ApConfigs.push(...ApConfigs);

              this.hasApVendor = !!ApConfigs.find((ApConfig) => {
                const { ApVendors } = ApConfig;
                if (Array.isArray(ApVendors)) {
                  return ApVendors.length > 0;
                }
                return false;
              });
            }

            const ComplianceSetting = ComplianceSettings[0];
            const BusinessComplianceSetting = BusinessComplianceSettings[0];

            if (isUsingNewBusinessVerification && !BusinessComplianceSetting) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Client is missing required compliance settings for Vendor.'
              );
            }

            if (!isUsingNewBusinessVerification && !ComplianceSetting) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Client is missing required compliance settings for Vendor.'
              );
            }

            const {
              hasBusinessReportData,
              ComplianceFinding,
              StreetAddress,
              MailingAddress,
              ComplianceContact
            } = Vendor;

            if (!StreetAddress) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Vendor is missing street address information.',
                { returnError: true }
              );
            }

            if (!MailingAddress) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Vendor is missing Mailing Address information.',
                { returnError: true }
              );
            }

            const { country } = StreetAddress;

            if (!country) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Address is missing country information.',
                { returnError: true }
              );
            }

            if (ComplianceFinding) {
              this.ComplianceFindingForm.patchValue(ComplianceFinding);
            }

            this.LocalizationInfo =
              this.UtilityService.getLocalizedValuesMap(country);

            this.recalculateComplianceResults(ComplianceResults);

            Object.assign(this, {
              hasBusinessReportData,
              ComplianceSetting: ComplianceSetting || undefined,
              BusinessComplianceSetting: BusinessComplianceSetting || undefined,
              ComplianceFinding,
              vendorRiskLevel,
              Vendor,
              ComplianceContact,
              StreetAddress,
              MailingAddress
            });

            this.SharedDataServiceSubscription =
              this.SharedDataService.listenForEvent<
                ViveProgress | null | ComplianceReviewAction
              >().subscribe({
                next: ({ eventName, data }) => {
                  if (
                    eventName === 'SetReviewAction' &&
                    data &&
                    !(data instanceof ViveProgress)
                  ) {
                    this.action = data;
                    this.SharedDataService.sendEvent(
                      'GetComplianceProgress',
                      new ViveProgress({ type: 'Vendor Info' })
                    );
                    this._cdr.markForCheck();
                  }

                  if (
                    new Set([
                      'SetComplianceProgress',
                      'SetComplianceProgressAfterUpdate'
                    ]).has(eventName) &&
                    (data instanceof ViveProgress || data === null)
                  ) {
                    this.ComplianceProgress = data;

                    const needsToRunBusinessVerification = !!(
                      this.ComplianceProgress &&
                      this.ComplianceProgress.status === 'Reviewing' &&
                      (!this.Vendor?.lastRanBusinessVerificationReportDate ||
                        this.isReportElevenMonthsOldOrOlder(
                          this.Vendor.lastRanBusinessVerificationReportDate
                        ))
                    );

                    if (!needsToRunBusinessVerification) {
                      this.displayAutoApproveModal();
                    }

                    // Set compliance progress if running compliance review.
                    // Only run business verification this when requesting for compliance progress initially.
                    // Do not run this if we're updating compliance progress from this component.
                    if (
                      this.action === 'review' &&
                      eventName !== 'SetComplianceProgressAfterUpdate'
                    ) {
                      if (
                        !this.ComplianceProgress ||
                        this.ComplianceProgress.status === null
                      ) {
                        this.SharedDataService.sendEvent(
                          'UpdateComplianceProgress',
                          new ViveProgress({
                            status: 'Reviewing',
                            type: 'Vendor Info'
                          })
                        );
                      } else if (
                        // If the VIVE Admin is reviewing a Vendor and the last time the report
                        // was run was 11 months ago, run business verification.
                        needsToRunBusinessVerification
                      ) {
                        this.runBusinessVerification();
                      }
                    }
                    this._cdr.markForCheck();
                  }

                  if (eventName === 'CheckForPassedFindings') {
                    this.SharedDataService.sendEvent(
                      'RespondIfPassedFindings',
                      this.checkForPassedFindings()
                    );
                    this._cdr.markForCheck();
                  }
                }
              });

            this.SharedDataService.sendEvent('GetReviewAction');
            this._cdr.markForCheck();
          }
        });
      }
    });

    this.getStuckRequestJobs();
  }
}
